import React, { useEffect, useState } from 'react';
import { FaTimes, FaFileAlt, FaUser, FaClock, FaCheckCircle, FaExclamationTriangle, FaRedo, FaUpload, FaDownload, FaHistory, FaEye, FaPaperPlane, FaSync, FaGripVertical } from 'react-icons/fa';
import { DocumentInstance, DocumentEvent, DocumentVersion, fetchDocumentEvents, resolveDocumentPdfUrl } from '../services/DocumentService';
import { rebuildBundle, BundleItem, BundleDetail } from '../services/BundleService';
import { absolutizePdfUrl } from '../../../utils/pdfUtils';
import axiosInstance from '../../../config/axios';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { colors, shadows } from '../styles/personalakteStyles';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('de-DE');
};

interface SortableItemRowProps {
  item: BundleItem;
  idx: number;
}

function SortableItemRow({ item, idx }: SortableItemRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.document_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: isDragging ? colors.borderLight : (item.kind === 'MAIN' ? colors.infoLight : item.kind === 'COVER' ? colors.warningLight : 'transparent'),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td style={{ padding: '8px 12px', cursor: 'grab' }} {...attributes} {...listeners}>
        <FaGripVertical size={14} color={colors.textLight} />
      </td>
      <td style={{ padding: '8px 12px', fontWeight: 500 }}>
        {idx + 1}
      </td>
      <td style={{ padding: '8px 12px' }}>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            backgroundColor: item.kind === 'COVER' ? colors.warning : item.kind === 'MAIN' ? colors.info : colors.warning,
            color: 'white',
          }}
        >
          {item.kind}
        </span>
      </td>
      <td style={{ padding: '8px 12px', color: colors.navy }}>
        {item.title}
      </td>
      <td style={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => {
              const url = item.pdf_url;
              if (url) {
                window.open(absolutizePdfUrl(url), '_blank', 'noopener,noreferrer');
              }
            }}
            style={{
              padding: '4px 8px',
              fontSize: 12,
              backgroundColor: colors.info,
              color: 'white',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            title="Öffnen"
          >
            <FaEye size={12} />
            Öffnen
          </button>
        </div>
      </td>
    </tr>
  );
}

interface DocumentDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentInstance | null;
  employeeId: number;
  onRegenerate?: (doc: DocumentInstance) => void;
  onUploadSigned?: (doc: DocumentInstance) => void;
  onDownload?: (doc: DocumentInstance) => void;
}

export function DocumentDetailDrawer({ isOpen, onClose, document, employeeId, onRegenerate, onUploadSigned, onDownload }: DocumentDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<'preview' | 'events' | 'versions' | 'bundle'>('preview');
  const [events, setEvents] = useState<DocumentEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [rebuildCoverPage, setRebuildCoverPage] = useState(true);
  const [rebuildCombinedPdf, setRebuildCombinedPdf] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildWarnings, setRebuildWarnings] = useState<string[]>([]);
  const [sortedItems, setSortedItems] = useState<BundleItem[]>([]);
  const [itemsChanged, setItemsChanged] = useState(false);

  const versions = document?.versions || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (document?.bundle?.items) {
      const sorted = [...document.bundle.items].sort((a, b) => a.order - b.order);
      setSortedItems(sorted);
      setItemsChanged(false);
    }
  }, [document?.bundle?.items]);

  const getSentDate = (): string | null | undefined => {
    if (document?.esign_sent_at) {
      return document.esign_sent_at;
    }
    const sentEvent = events.find(e => e.event_type === 'sent');
    return sentEvent?.timestamp;
  };

  const getSignedDate = (): string | null | undefined => {
    if (document?.esign_signed_at) {
      return document.esign_signed_at;
    }
    const signedEvent = events.find(e => e.event_type === 'signed');
    return signedEvent?.timestamp;
  };

  const handleRebuild = async () => {
    if (!document?.bundle_id) {
      toast.error('Keine Bundle-ID vorhanden');
      return;
    }

    setRebuilding(true);
    setRebuildWarnings([]);
    try {
      const result = await rebuildBundle(document.bundle_id, {
        idempotency_key: uuidv4(),
        make_cover_page: rebuildCoverPage,
        make_combined_pdf: rebuildCombinedPdf,
      });


      if (result && document.bundle) {
        document.bundle.combined_pdf_url = result.combined_pdf_url;
        document.bundle.items = result.items;
        setSortedItems([...result.items].sort((a, b) => a.order - b.order));
      }

      const warnings = result.merge?.warnings || [];
      if (warnings.length > 0) {
        setRebuildWarnings(warnings);
        toast.warning('Bundle mit Warnungen neu erstellt');
      } else {
        toast.success('Bundle erfolgreich neu erstellt');
      }

      if (onRegenerate) {
        onRegenerate(document);
      }
    } catch (err: any) {
      console.error('[BundleTab] Failed to rebuild bundle:', err);
      const warnings = err?.response?.data?.merge?.warnings || [];
      if (warnings.length > 0) {
        setRebuildWarnings(warnings);
        toast.warning('Bundle mit Warnungen erstellt');
      } else {
        toast.error('Fehler beim Neu-Erstellen des Bundles');
      }
    } finally {
      setRebuilding(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }


    setSortedItems((items) => {
      const oldIndex = items.findIndex((item) => item.document_id === active.id);
      const newIndex = items.findIndex((item) => item.document_id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        console.error('[BundleTab] DnD: Item not found', { oldIndex, newIndex });
        return items;
      }

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItemsChanged(true);
      return newItems;
    });
  };

  const handleSaveOrder = async () => {
    if (!document?.bundle_id) {
      toast.error('Keine Bundle-ID vorhanden');
      return;
    }

    const order_override = sortedItems.map(item => item.document_id);

    setRebuilding(true);
    try {
      const result = await rebuildBundle(document.bundle_id, {
        idempotency_key: uuidv4(),
        order_override,
        make_cover_page: rebuildCoverPage,
        make_combined_pdf: rebuildCombinedPdf,
      });


      if (result && document.bundle) {
        document.bundle.combined_pdf_url = result.combined_pdf_url;
        document.bundle.items = result.items;
        setSortedItems([...result.items].sort((a, b) => a.order - b.order));
      }

      const warnings = result.merge?.warnings || [];
      if (warnings.length > 0) {
        setRebuildWarnings(warnings);
        toast.warning('Reihenfolge gespeichert mit Warnungen');
      } else {
        toast.success('Reihenfolge gespeichert und Bundle neu erstellt');
      }

      setItemsChanged(false);

      if (onRegenerate) {
        onRegenerate(document);
      }
    } catch (err: any) {
      console.error('[BundleTab] Failed to save order:', err);
      const warnings = err?.response?.data?.merge?.warnings || [];
      if (warnings.length > 0) {
        setRebuildWarnings(warnings);
        toast.warning('Bundle mit Warnungen erstellt');
      } else {
        toast.error('Fehler beim Speichern der Reihenfolge');
      }
    } finally {
      setRebuilding(false);
    }
  };

  useEffect(() => {
    if (isOpen && document) {
      setLoading(true);
      setError('');

      fetchDocumentEvents(document.id)
        .then((data) => setEvents(data))
        .catch((err) => {
          console.error('Failed to load events:', err);
          setError('Failed to load document events');
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, document]);

  useEffect(() => {
    let blobUrl: string | null = null;

    const loadPreview = async () => {
      if (!isOpen || !document || activeTab !== 'preview') {
        setPreviewBlobUrl(null);
        return;
      }

      setLoadingPreview(true);
      setError('');
      setPreviewBlobUrl(null);

      try {

        // Support both bundle and bundle_summary from backend
        const bundleData = document.bundle_summary || document.bundle;

        // Prefer bundle combined PDF if available
        if (bundleData?.combined_pdf_url) {

          // Use axios with auth header for API endpoints
          const combinedUrl = bundleData.combined_pdf_url;

          const response = await axiosInstance.get(combinedUrl, { responseType: 'blob' });
          const blob = response.data;
          const url = URL.createObjectURL(blob);
          blobUrl = url;
          setPreviewBlobUrl(url);
        } else {
          // Fallback to single document PDF
          const blob = await resolveDocumentPdfUrl(document);
          const url = URL.createObjectURL(blob);
          blobUrl = url;
          setPreviewBlobUrl(url);
        }
      } catch (err: any) {
        console.error('[DocumentDrawer] Failed to load PDF:', err);

        let errorMsg = 'Failed to load PDF preview';

        // Try to parse JSON error from blob response
        if (err?.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const errorData = JSON.parse(text);
            errorMsg = errorData.detail || errorData.message || errorData.error || errorMsg;
            console.error('Backend error:', errorData);
          } catch (parseErr) {
            // If parsing fails, use default message
          }
        } else if (err?.response?.data?.detail) {
          errorMsg = err.response.data.detail;
        }

        if (err?.response?.status === 404) {
          errorMsg = `Preview not available: ${errorMsg}`;
        }

        setError(errorMsg);
        setPreviewBlobUrl(null);
      } finally {
        setLoadingPreview(false);
      }
    };

    loadPreview();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isOpen, document, activeTab]);

  const getEventIcon = (eventType: DocumentEvent['event_type']) => {
    const iconMap: Record<DocumentEvent['event_type'], React.ReactElement> = {
      created: <FaFileAlt size={16} color={colors.info} />,
      regenerated: <FaRedo size={16} color={colors.warning} />,
      sent: <FaCheckCircle size={16} color={colors.success} />,
      viewed: <FaClock size={16} color={colors.textMuted} />,
      signed: <FaCheckCircle size={16} color={colors.success} />,
      uploaded: <FaUpload size={16} color="#7b1fa2" />,
      downloaded: <FaDownload size={16} color={colors.info} />,
      signed_uploaded: <FaCheckCircle size={16} color={colors.success} />,
      error: <FaExclamationTriangle size={16} color={colors.danger} />,
    };
    return iconMap[eventType] || <FaClock size={16} color={colors.textMuted} />;
  };

  const getEventTitle = (event: DocumentEvent) => {
    const titleMap: Record<DocumentEvent['event_type'], string> = {
      created: 'Document Created',
      regenerated: 'Document Regenerated',
      sent: 'Sent for eSign',
      viewed: 'Document Viewed',
      signed: 'Document Signed',
      uploaded: 'Signed Document Uploaded',
      downloaded: 'Document Downloaded',
      signed_uploaded: 'Signed PDF Uploaded',
      error: 'Error Occurred',
    };
    return titleMap[event.event_type] || event.event_type;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1099,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Drawer */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '700px',
          backgroundColor: colors.bgCard,
          boxShadow: shadows.elevated,
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
        }}
      >
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.navy }}>
            Document Details
          </h3>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => setActiveTab('preview')}
              style={{
                padding: '8px 16px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'preview' ? `3px solid ${colors.magenta}` : '3px solid transparent',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: activeTab === 'preview' ? 600 : 400,
                color: activeTab === 'preview' ? colors.navy : colors.textLight,
              }}
            >
              <FaEye size={14} style={{ marginRight: 6 }} />
              Preview
            </button>
            <button
              onClick={() => setActiveTab('events')}
              style={{
                padding: '8px 16px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'events' ? `3px solid ${colors.magenta}` : '3px solid transparent',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: activeTab === 'events' ? 600 : 400,
                color: activeTab === 'events' ? colors.navy : colors.textLight,
              }}
            >
              <FaClock size={14} style={{ marginRight: 6 }} />
              Events
            </button>
            <button
              onClick={() => setActiveTab('versions')}
              style={{
                padding: '8px 16px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'versions' ? `3px solid ${colors.magenta}` : '3px solid transparent',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: activeTab === 'versions' ? 600 : 400,
                color: activeTab === 'versions' ? colors.navy : colors.textLight,
              }}
            >
              <FaHistory size={14} style={{ marginRight: 6 }} />
              Versions
            </button>
            {document?.bundle_id && (
              <button
                onClick={() => setActiveTab('bundle')}
                style={{
                  padding: '8px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === 'bundle' ? `3px solid ${colors.magenta}` : '3px solid transparent',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: activeTab === 'bundle' ? 600 : 400,
                  color: activeTab === 'bundle' ? colors.navy : colors.textLight,
                }}
              >
                <FaFileAlt size={14} style={{ marginRight: 6 }} />
                Bundle
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {document && (
              <>
                {onRegenerate && (document.status === 'READY' || document.status === 'SIGNED') && (
                  <button
                    onClick={() => onRegenerate(document)}
                    title="Regenerate"
                    style={{
                      padding: '6px',
                      background: colors.warningLight,
                      color: colors.warning,
                      border: `1px solid ${colors.warning}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FaRedo size={12} />
                  </button>
                )}
                {onUploadSigned && document.status === 'READY' && (
                  <button
                    onClick={() => onUploadSigned(document)}
                    title="Upload Signed"
                    style={{
                      padding: '6px',
                      background: colors.successLight,
                      color: colors.success,
                      border: `1px solid ${colors.success}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FaUpload size={12} />
                  </button>
                )}
              </>
            )}
            <button
              onClick={onClose}
              title="Close"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                color: colors.textMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FaTimes size={14} />
            </button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: activeTab === 'preview' ? 'hidden' : 'auto', padding: activeTab === 'preview' ? 0 : '12px 24px 0 24px', display: 'flex', flexDirection: 'column' }}>
        {document && (
          <>

              {loading && (
                <div style={{ textAlign: 'center', padding: 20, color: colors.textMuted }}>
                  {activeTab === 'events' ? 'Loading events...' : 'Loading versions...'}
                </div>
              )}

              {error && (
                <div
                  style={{
                    padding: 12,
                    backgroundColor: colors.dangerLight,
                    color: colors.danger,
                    borderRadius: 6,
                    fontSize: 13,
                    border: `1px solid ${colors.danger}`,
                  }}
                >
                  {error}
                </div>
              )}

              {activeTab === 'preview' && document && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {loadingPreview && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: colors.textMuted, fontSize: 14 }}>
                      PDF-Vorschau wird geladen...
                    </div>
                  )}
                  {!loadingPreview && error && (
                    <div style={{ padding: 20, textAlign: 'center' }}>
                      <div style={{ marginBottom: 12, color: colors.warning, fontSize: 16, fontWeight: 500 }}>
                        PDF-Vorschau nicht verfügbar
                      </div>
                      <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
                        Dieses Dokument hat möglicherweise keine generierte Version oder wurde vor der Versionierung erstellt.
                      </div>
                      {document.pdf_url && (
                        <a
                          href={absolutizePdfUrl(document.pdf_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 16px',
                            backgroundColor: colors.info,
                            color: 'white',
                            borderRadius: 10,
                            textDecoration: 'none',
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                        >
                          <FaEye size={14} />
                          In neuem Tab öffnen
                        </a>
                      )}
                    </div>
                  )}
                  {!loadingPreview && !error && previewBlobUrl && (
                    <iframe
                      src={previewBlobUrl}
                      width="100%"
                      style={{ flex: 1, border: 0, minHeight: 0 }}
                      title="PDF Preview"
                    />
                  )}
                  {!loadingPreview && !error && !previewBlobUrl && (
                    <div style={{ padding: 20, textAlign: 'center', color: colors.textLight }}>
                      <div style={{ marginBottom: 8, fontSize: 14 }}>Keine PDF-Vorschau verfügbar</div>
                      <div style={{ fontSize: 12, color: colors.textLight }}>
                        Bitte generieren Sie das Dokument neu, um eine Vorschau zu erhalten.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!loading && !error && activeTab === 'events' && (
                <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  <div style={{ marginBottom: 32 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 16 }}>
                      Document Information
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Position</div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: colors.navy }}>
                          {document.position_title || document.template_name || 'Position unbekannt'}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Document ID / Version</div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: colors.navy, fontFamily: 'monospace' }}>
                          #{document.id} v{document.version}
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: 10,
                              padding: '2px 6px',
                              backgroundColor: colors.info,
                              color: 'white',
                              borderRadius: 4,
                              fontWeight: 600,
                              fontFamily: 'sans-serif',
                            }}
                          >
                            CURRENT
                          </span>
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Status</div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: colors.navy }}>
                          {document.status}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Created At</div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: colors.navy }}>
                          {formatDate(document.created_at)}
                        </div>
                      </div>

                      {document.contains_compensation && (
                        <div>
                          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Compensation</div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: colors.warning }}>
                            🔒 Contains compensation data
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginBottom: 32 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 16 }}>
                      Document Lifecycle
                    </h4>
                    <div style={{ position: 'relative' }}>
                      <div
                        style={{
                          position: 'absolute',
                          left: 11,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          backgroundColor: colors.border,
                        }}
                      />

                      <div style={{ position: 'relative', paddingLeft: 40, paddingBottom: 24 }}>
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 2,
                            width: 24,
                            height: 24,
                            backgroundColor: colors.success,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FaCheckCircle size={14} color="white" />
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 4 }}>
                            Created
                          </div>
                          <div style={{ fontSize: 12, color: colors.textMuted }}>
                            {formatDate(document.created_at)}
                          </div>
                        </div>
                      </div>

                      <div style={{ position: 'relative', paddingLeft: 40, paddingBottom: 24 }}>
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 2,
                            width: 24,
                            height: 24,
                            backgroundColor: getSentDate() ? colors.success : colors.border,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {getSentDate() ? (
                            <FaCheckCircle size={14} color="white" />
                          ) : (
                            <FaClock size={14} color={colors.textLight} />
                          )}
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: getSentDate() ? colors.navy : colors.textLight,
                              marginBottom: 4,
                            }}
                          >
                            Sent to employee
                          </div>
                          <div style={{ fontSize: 12, color: colors.textMuted }}>
                            {formatDate(getSentDate())}
                          </div>
                        </div>
                      </div>

                      <div style={{ position: 'relative', paddingLeft: 40 }}>
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 2,
                            width: 24,
                            height: 24,
                            backgroundColor: getSignedDate() ? colors.success : colors.border,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {getSignedDate() ? (
                            <FaCheckCircle size={14} color="white" />
                          ) : (
                            <FaClock size={14} color={colors.textLight} />
                          )}
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: getSignedDate() ? colors.navy : colors.textLight,
                              marginBottom: 4,
                            }}
                          >
                            Signed by Employee
                          </div>
                          <div style={{ fontSize: 12, color: colors.textMuted }}>
                            {formatDate(getSignedDate())}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!loading && !error && activeTab === 'versions' && versions.length === 0 && (
                <div style={{ textAlign: 'center', padding: 20, color: colors.textLight, fontSize: 13 }}>
                  <div style={{ marginBottom: 8, fontSize: 14 }}>Keine Versionen verfügbar</div>
                  <div style={{ fontSize: 12, color: colors.textLight }}>
                    Dieses Dokument wurde möglicherweise vor der Versionierung erstellt.
                  </div>
                </div>
              )}

              {!loading && !error && activeTab === 'versions' && versions.length > 0 && (
                <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                          Version
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                          Rendered
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                          Size
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                          Hash
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map((version) => (
                        <tr
                          key={`ver-${version.id ?? version.version}`}
                          style={{
                            borderBottom: `1px solid ${colors.border}`,
                            backgroundColor: version.is_current ? colors.infoLight : 'transparent',
                          }}
                        >
                          <td style={{ padding: '10px 12px', fontWeight: version.is_current ? 600 : 400 }}>
                            v{version.version}
                            {version.is_current && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: 11,
                                  padding: '2px 6px',
                                  backgroundColor: colors.info,
                                  color: 'white',
                                  borderRadius: 4,
                                  fontWeight: 600,
                                }}
                              >
                                CURRENT
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', color: colors.textMuted }}>
                            {formatDate(version.rendered_at)}
                          </td>
                          <td style={{ padding: '10px 12px', color: colors.textMuted, fontFamily: 'monospace', fontSize: 12 }}>
                            {version.pdf_size_bytes
                              ? `${(version.pdf_size_bytes / 1024).toFixed(1)} KB`
                              : '-'}
                          </td>
                          <td style={{ padding: '10px 12px', color: colors.textMuted, fontFamily: 'monospace', fontSize: 11 }}>
                            {version.pdf_hash ? version.pdf_hash.substring(0, 12) + '...' : '-'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => {
                                  const url = version.preview_url || version.download_url || document?.pdf_url;
                                  if (url) {
                                    window.open(absolutizePdfUrl(url), '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 12,
                                  backgroundColor: colors.info,
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 10,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                                title="Preview"
                              >
                                <FaEye size={12} />
                                Preview
                              </button>
                              <button
                                onClick={() => {
                                  const url = version.download_url || version.preview_url || document?.pdf_url;
                                  if (url) {
                                    window.open(absolutizePdfUrl(url), '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 12,
                                  backgroundColor: '#388e3c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 10,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                                title="Download"
                              >
                                <FaDownload size={12} />
                                Download
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'bundle' && document?.bundle && (
                <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '12px 0' }}>
                  {rebuildWarnings.length > 0 && (
                    <div
                      style={{
                        padding: '12px 16px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: 6,
                        marginBottom: 16,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#856404', marginBottom: 8 }}>
                        ⚠️ Rebuild-Warnungen:
                      </div>
                      {rebuildWarnings.map((warning, idx) => (
                        <div key={idx} style={{ fontSize: 13, color: '#856404', marginBottom: 4 }}>
                          • {warning}
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 12 }}>
                      Bundle-Informationen
                    </h4>
                    <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 8 }}>
                      Bundle ID: {document.bundle.id} | Anhänge: {document.bundle.attachments_count}
                    </div>
                  </div>

                  {document.bundle.items.length > 1 && (
                    <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: 6 }}>
                      <h5 style={{ fontSize: 13, fontWeight: 600, color: colors.navy, marginBottom: 12 }}>
                        Kombi-PDF neu erstellen
                      </h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: colors.textMuted, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={rebuildCoverPage}
                            onChange={(e) => setRebuildCoverPage(e.target.checked)}
                            style={{ marginRight: 8, cursor: 'pointer', width: 16, height: 16 }}
                          />
                          Cover Page erzeugen
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: colors.textMuted, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={rebuildCombinedPdf}
                            onChange={(e) => setRebuildCombinedPdf(e.target.checked)}
                            style={{ marginRight: 8, cursor: 'pointer', width: 16, height: 16 }}
                          />
                          PDFs kombinieren
                        </label>
                      </div>
                      <button
                        onClick={handleRebuild}
                        disabled={rebuilding}
                        style={{
                          padding: '8px 16px',
                          fontSize: 14,
                          backgroundColor: rebuilding ? '#ccc' : '#f57c00',
                          color: 'white',
                          border: 'none',
                          borderRadius: 10,
                          cursor: rebuilding ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                        title="Bundle neu erstellen"
                      >
                        <FaSync size={14} className={rebuilding ? 'fa-spin' : ''} />
                        {rebuilding ? 'Wird neu erstellt...' : 'Kombi-PDF neu erstellen'}
                      </button>
                    </div>
                  )}

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <h5 style={{ fontSize: 13, fontWeight: 600, color: colors.navy, margin: 0 }}>
                        Enthaltene Dokumente
                      </h5>
                      {itemsChanged && (
                        <button
                          onClick={handleSaveOrder}
                          disabled={rebuilding}
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                            backgroundColor: rebuilding ? '#ccc' : '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: 10,
                            cursor: rebuilding ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                          title="Reihenfolge speichern"
                        >
                          <FaSync size={12} className={rebuilding ? 'fa-spin' : ''} />
                          Reihenfolge speichern
                        </button>
                      )}
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy, width: 40 }}>

                            </th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                              #
                            </th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                              Typ
                            </th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                              Name
                            </th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: colors.navy }}>
                              Aktionen
                            </th>
                          </tr>
                        </thead>
                        <SortableContext
                          items={sortedItems.map(item => item.document_id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <tbody>
                            {sortedItems.map((item, idx) => (
                              <SortableItemRow key={item.document_id} item={item} idx={idx} />
                            ))}
                          </tbody>
                        </SortableContext>
                      </table>
                    </DndContext>
                  </div>

                  {document.bundle.combined_pdf_url && (
                    <div style={{ marginTop: 16 }}>
                      <h5 style={{ fontSize: 13, fontWeight: 600, color: colors.navy, marginBottom: 8 }}>
                        Kombiniertes PDF
                      </h5>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => {
                            if (document.bundle?.combined_pdf_url) {
                              window.open(absolutizePdfUrl(document.bundle.combined_pdf_url), '_blank', 'noopener,noreferrer');
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            fontSize: 14,
                            backgroundColor: '#388e3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: 10,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                          title="Kombiniertes PDF anzeigen"
                        >
                          <FaEye size={14} />
                          Kombiniertes PDF anzeigen
                        </button>
                        <a
                          href={absolutizePdfUrl(document.bundle.combined_pdf_url)}
                          download={document.bundle.suggested_file_name || 'combined.pdf'}
                          style={{
                            padding: '8px 16px',
                            fontSize: 14,
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: 10,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            textDecoration: 'none',
                          }}
                          title="Download"
                        >
                          <FaDownload size={14} />
                          Download
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
          </>
        )}
      </div>
    </div>
    </>
  );
}
